import { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ExternalLink } from "lucide-react";
import SortableItem from "../components/SortableItem";
import { api, type LocalesResponse } from "../api";
import { useToast } from "../components/Toast";
import type { Category, Entry } from "../../shared/types";

export default function Categories() {
  const [cats, setCats] = useState<Category[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [cfg, setCfg] = useState<LocalesResponse | null>(null);
  const [activeCat, setActiveCat] = useState<string>("");
  const [poolSearch, setPoolSearch] = useState("");
  const [orphansOnly, setOrphansOnly] = useState(false);
  const toast = useToast();

  useEffect(() => {
    Promise.all([api.listCategories(), api.listEntries(), api.getLocales()]).then(
      ([c, e, l]) => {
        setCats(c);
        setEntries(e);
        setCfg(l);
        if (c.length > 0) setActiveCat(c[0].cat_id);
      }
    );
  }, []);

  const cur = cats.find((c) => c.cat_id === activeCat);
  const entryById = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
  const orphanIds = useMemo(() => {
    const seen = new Set<string>();
    for (const c of cats) for (const id of c.countries) seen.add(id);
    return new Set(entries.map((e) => e.id).filter((id) => !seen.has(id)));
  }, [cats, entries]);

  const pool = useMemo(() => {
    const q = poolSearch.trim().toLowerCase();
    return entries.filter((e) => {
      if (orphansOnly && !orphanIds.has(e.id)) return false;
      if (!q) return true;
      return e.id.toLowerCase().includes(q) || (e.titles.en ?? "").toLowerCase().includes(q);
    });
  }, [entries, poolSearch, orphansOnly, orphanIds]);

  async function updateMembership(next: string[]) {
    if (!cur) return;
    const prevCats = cats;
    setCats((cs) => cs.map((c) => (c.cat_id === cur.cat_id ? { ...c, countries: next } : c)));
    try {
      await api.putCategoryMembers(cur.cat_id, next);
    } catch (err) {
      setCats(prevCats);
      toast.show("error", (err as Error).message);
    }
  }

  function onDragEnd(ev: DragEndEvent) {
    if (!cur || !ev.over || ev.active.id === ev.over.id) return;
    const oldIndex = cur.countries.indexOf(String(ev.active.id));
    const newIndex = cur.countries.indexOf(String(ev.over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(cur.countries, oldIndex, newIndex);
    updateMembership(next);
  }

  function removeFromCategory(id: string) {
    if (!cur) return;
    updateMembership(cur.countries.filter((x) => x !== id));
  }

  function addToCategory(id: string) {
    if (!cur || cur.countries.includes(id)) return;
    updateMembership([...cur.countries, id]);
  }

  return (
    <div>
      {cfg && (
        <div className="mb-3 text-xs text-gh-muted">
          Public JSON:{" "}
          <a
            href={`${cfg.publicOrigin}/categories.json`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gh-accent hover:underline inline-flex items-center gap-1"
          >
            {cfg.publicOrigin}/categories.json
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
      <div className="grid grid-cols-12 gap-4">
      <div className="col-span-3 bg-gh-surface border border-gh-border rounded p-3">
        <h3 className="text-xs uppercase tracking-wide text-gh-muted mb-2">Categories</h3>
        {cats.map((c) => (
          <button
            key={c.cat_id}
            onClick={() => setActiveCat(c.cat_id)}
            className={`block w-full text-left px-2 py-1 rounded text-sm ${
              c.cat_id === activeCat ? "bg-gh-bg font-medium" : "hover:bg-gh-bg"
            }`}
          >
            {c.cat_id}
            <span className="text-xs text-gh-muted ml-2">({c.countries.length})</span>
          </button>
        ))}
      </div>

      <div className="col-span-5 bg-gh-surface border border-gh-border rounded p-3">
        <h3 className="text-xs uppercase tracking-wide text-gh-muted mb-2">
          {cur?.cat_id ?? "—"} — modes:{" "}
          <span className="text-gh-text">{cur?.mode.join(", ") ?? ""}</span>
        </h3>
        <p className="text-xs text-gh-muted mb-2">
          Edit cat_id and modes via Filebrowser; CMS owns memberships only.
        </p>
        {cur && (
          <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={cur.countries} strategy={verticalListSortingStrategy}>
              {cur.countries.map((id) => {
                const e = entryById.get(id);
                return (
                  <SortableItem key={id} id={id} onRemove={() => removeFromCategory(id)}>
                    <div className="flex items-center gap-2">
                      {e && (
                        <img
                          src={`/${e.images.flag}`}
                          alt=""
                          className="w-6 h-4 object-contain"
                        />
                      )}
                      <span className="font-mono text-xs">{id}</span>
                      <span className="text-xs text-gh-muted">{e?.titles.en ?? "(missing)"}</span>
                    </div>
                  </SortableItem>
                );
              })}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="col-span-4 bg-gh-surface border border-gh-border rounded p-3">
        <h3 className="text-xs uppercase tracking-wide text-gh-muted mb-2">Pool</h3>
        <input
          type="text"
          placeholder="search id or EN title"
          value={poolSearch}
          onChange={(e) => setPoolSearch(e.target.value)}
          className="w-full px-2 py-1 rounded border border-gh-border text-sm mb-2"
        />
        <label className="text-xs text-gh-muted block mb-2">
          <input
            type="checkbox"
            checked={orphansOnly}
            onChange={(e) => setOrphansOnly(e.target.checked)}
            className="mr-1"
          />
          Orphans only
        </label>
        <div className="max-h-[60vh] overflow-y-auto">
          {pool.map((e) => (
            <button
              key={e.id}
              onClick={() => addToCategory(e.id)}
              className="block w-full text-left px-2 py-1 rounded text-xs hover:bg-gh-bg flex items-center gap-2"
            >
              <img src={`/${e.images.flag}`} alt="" className="w-6 h-4 object-contain" />
              <span className="font-mono">{e.id}</span>
              <span className="text-gh-muted">{e.titles.en}</span>
            </button>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
